import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const shuffleArray = (array) => {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const GuessTheYear = ({ events }) => {
  const { t } = useTranslation();

  const [filteredEvents, setFilteredEvents] = useState([]);
  const [index, setIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  useEffect(() => {
    setFilteredEvents(shuffleArray(events));
    setIndex(0);
    setShowAnswer(false);
    setSelectedYear(null);
    setScore(0);
    setStreak(0);
    setBestStreak(0);
  }, [events]);

  const current = filteredEvents[index];
  const generateOptions = () => {
    const incorrectYears = events
      .filter((e) => e._id !== current._id && e.year !== current.year)
      .map((e) => e.year)
      .filter((year, i, arr) => arr.indexOf(year) === i);

    while (incorrectYears.length < 3) {
      const randYear = Math.floor(Math.random() * 1000) + 1000;
      if (!incorrectYears.includes(randYear) && randYear !== current.year) {
        incorrectYears.push(randYear);
      }
    }

    return shuffleArray([
      current.year,
      ...shuffleArray(incorrectYears).slice(0, 3),
    ]);
  };

  const options = current ? generateOptions() : [];

  const handleAnswer = (year) => {
    setSelectedYear(year);
    setShowAnswer(true);
    if (year === current.year) {
      const newStreak = streak + 1;
      setScore(score + 1);
      setStreak(newStreak);
      if (newStreak > bestStreak) setBestStreak(newStreak);
    } else {
      setStreak(0);
    }
  };

  const nextQuestion = () => {
    setIndex((index + 1) % filteredEvents.length);
    setShowAnswer(false);
    setSelectedYear(null);
  };

  if (!current) {
    return <p className="text-center text-gray-400">{t('quiz.guess.noEvents')}</p>;
  }
  return (
    <div className="max-w-3xl mx-auto mt-10 p-6 bg-gray-900 text-white rounded-lg shadow-2xl border border-gray-800">
      <h2 className="text-3xl font-bold mb-6 text-center text-purple-400">{t('quiz.mode.guessYear')}</h2>

      <div className="bg-gray-800 p-4 rounded-lg shadow mb-4">
        <h3 className="text-xl font-semibold text-purple-300 mb-2">{current.title}</h3>
        <p className="text-sm text-gray-300">{current.description}</p>
      </div>

      <div className="text-center mb-6">
        <span className="text-sm text-gray-400">
          <strong>{t('quiz.guess.score')}:</strong> {score} | <strong>{t('quiz.guess.streak')}:</strong> {streak} | <strong>{t('quiz.guess.best')}:</strong> {bestStreak}
        </span>
      </div>

      <div className="flex flex-col gap-3">
        {options.map((year, i) => {
          const isCorrect = showAnswer && year === current.year;
          const isWrong = showAnswer && year === selectedYear && year !== current.year;

          return (
            <button
              key={i}
              onClick={() => handleAnswer(year)}
              disabled={showAnswer}
              className={`px-4 py-3 rounded border font-semibold transition text-lg ${
                isCorrect
                  ? 'bg-green-700 text-white border-green-500'
                  : isWrong
                  ? 'bg-red-700 text-white border-red-500'
                  : 'bg-gray-800 text-white border-gray-600 hover:bg-gray-700'
              }`}
            >
              {year}
            </button>
          );
        })}
      </div>

      {showAnswer && (
        <div className="mt-6 text-center">
          {selectedYear === current.year ? (
            <p className="text-green-400 font-semibold text-lg">{t('quiz.guess.correct')}</p>
          ) : (
            <p className="text-red-400 font-semibold text-lg">
              {t('quiz.guess.wrong')} <strong>{current.year}</strong>
            </p>
          )}
          <button
            onClick={nextQuestion}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          >
            {t('quiz.guess.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default GuessTheYear;
